import { Announcements } from 'modules/announcements';
import { SliderContainer } from 'modules/nav/components/SliderContainer';
import styled from 'styled-components';
import { backgroundColors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { Header } from './Header';
import { PropsWithChildren } from 'react';

const App = styled.div<{ navBarOffset: string }>`
  background: ${backgroundColors.backgroundPrimary};
  padding: ${spacing.layoutLRPadding};
  padding-top: ${({ navBarOffset }) => navBarOffset};

  &[data-full-height='true'] {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  }
`;

const Content = styled.main`
  padding-top: ${spacing.layoutTopPadding};

  [data-full-height='true'] & {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
`;

export const DefaultLayout = ({
  children,
  isFullHeight = false,
}: PropsWithChildren<{ isFullHeight?: boolean }>) => {
  const height = useNavBarHeight();
  const navBarOffset = pxToRem(height);

  return (
    <App data-full-height={isFullHeight} data-global-nav={true} navBarOffset={navBarOffset}>
      <Header />
      <Announcements />
      <Content data-id="routes-container">{children}</Content>
      <SliderContainer />
    </App>
  );
};

export const DefaultLayoutFullHeight = ({ children }: PropsWithChildren) => (
  <DefaultLayout isFullHeight>{children}</DefaultLayout>
);

/**
 * Modified layout used to solve a few issues
 * - Moves Announcements into the page content so that it isn't hidden
 *   behind PageHeaders when fixed to top of page (new Billing and Project Settings pages)
 * - Applies the same "full height" styes as the `DefaultLayoutFullHeight` above
 */
export const DefaultLayoutModernized = ({ children }: PropsWithChildren) => {
  const height = useNavBarHeight();
  // offset padding and top positions based on Content padding-top (above)
  const navBarOffset = pxToRem(height - 20);

  return (
    <App data-full-height="true" navBarOffset={navBarOffset}>
      <Header />
      <Content data-id="routes-container">
        <Announcements />
        {children}
      </Content>
      <SliderContainer />
    </App>
  );
};
